import { useWatch } from 'react-hook-form';
import type { AutocompleteOption } from '@/Components/shadcn';
import { useRefineSearchFormContext } from '../useRefineSearchForm';
import { getSynonyms } from './jobTitleSynonymsUtils';
import { FILTERS_LIMIT } from '../FiltersLimitConsts';

interface UseJobTitleSynonymsAdderResult {
    addSynonyms: () => void;
    hasJobTitles: boolean;
    isLimitHit: boolean;
}

/**
 * Returns a handler to manually add synonyms for all current job title chips,
 * along with visibility and disabled state for the "Add similar" button.
 */
const useJobTitleSynonymsAdder = (): UseJobTitleSynonymsAdderResult => {
    const form = useRefineSearchFormContext();
    const jobTitles = useWatch({ control: form.control, name: 'jobTitles' });

    const addSynonyms = () => {
        const currentTitles: AutocompleteOption[] = form.getValues('jobTitles');
        const currentLabels = new Set(currentTitles.map((t) => t.label.toLowerCase()));

        const synonymsToAdd: AutocompleteOption[] = [];

        currentTitles.forEach((title) => {
            const synonyms = getSynonyms(title.label);

            synonyms.forEach((synonym) => {
                const synonymLower = synonym.toLowerCase();
                if (!currentLabels.has(synonymLower)) {
                    synonymsToAdd.push({ label: synonym });
                    currentLabels.add(synonymLower);
                }
            });
        });

        if (synonymsToAdd.length === 0) return;

        const remaining = FILTERS_LIMIT - currentTitles.length;
        if (remaining <= 0) return;

        form.setValue('jobTitles', [...currentTitles, ...synonymsToAdd.slice(0, remaining)], { shouldDirty: true });
    };

    return {
        addSynonyms,
        hasJobTitles: jobTitles.length > 0,
        isLimitHit: jobTitles.length >= FILTERS_LIMIT,
    };
};

export default useJobTitleSynonymsAdder;
